import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { authServiceInstance as authService } from '../../../services/authServiceInstance';
import { usePolling, UsePollingProps } from '@/libs/polling/usePolling';
import {
  Auth0DeviceCodeError,
  isAuthTokens,
  isDeviceCodeError,
} from '../authTypes';

const DEFAULT_POLL_INTERVAL_MS = 5000; // Default polling interval in milliseconds
const POLLING_BACKOFF_STEP_MS = 5000;
const MAX_POLL_INTERVAL_MS = 30000; // 30s cap to avoid excessive delay

export interface DeviceCodeState {
  deviceCode: string | null;
  userCode: string;
  qrCodeUrl: string | null;
  isPolling: boolean;
  timeRemaining: number;
  error: string | null;
  tokenPollInterval: number; // Auth0 response Interval in milliseconds for polling
  status: DeviceCodeProgress;
}

export type DeviceCodeStateUpdate = Pick<
  DeviceCodeState,
  | 'deviceCode'
  | 'error'
  | 'isPolling'
  | 'qrCodeUrl'
  | 'timeRemaining'
  | 'userCode'
>;

export enum DeviceCodeProgress {
  IDLE = 'idle',
  DEVICE_CODE_REQUESTED = 'device_code_requested',
  DEVICE_CODE_RECEIVED = 'device_code_received',
  DEVICE_CODE_EXPIRED = 'device_code_expired',
  TOKEN_REQUESTED = 'token_requested',
  TOKEN_RECEIVED = 'token_received',
  CANCELLED = 'cancelled',
}

export interface UseDeviceCodeAuthProps {
  onStateChange?: (state: DeviceCodeStateUpdate) => void;
}

interface UseDeviceCodeAuth
  extends Pick<
    DeviceCodeState,
    | 'deviceCode'
    | 'userCode'
    | 'qrCodeUrl'
    | 'isPolling'
    | 'timeRemaining'
    | 'error'
    | 'status'
  > {
  initiateDeviceCodeFlow: (opts?: { retry: boolean }) => void;
  cancelPairing: () => void;
  resetDeviceCode: () => void;
}

export const useDeviceCodeAuth = ({
  onStateChange,
}: UseDeviceCodeAuthProps = {}): UseDeviceCodeAuth => {
  // Device code state
  const [deviceCode, setDeviceCode] = useState<string | null>(null);
  const [userCode, setUserCode] = useState<string>('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [tokenPollInterval, setTokenPollInterval] = useState<number>(0);
  const [status, setStatus] = useState<DeviceCodeProgress>(
    DeviceCodeProgress.IDLE,
  );

  // AbortController ref to cancel ongoing requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Helper function to abort current request and reset the ref
  const abortCurrentRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Reset function
  const resetDeviceCode = useCallback(() => {
    setDeviceCode(null);
    setUserCode('');
    setQrCodeUrl(null);
    setIsPolling(false);
    setTimeRemaining(0);
    setError(null);
    setTokenPollInterval(DEFAULT_POLL_INTERVAL_MS);
    setStatus(DeviceCodeProgress.IDLE);
  }, []);

  const getTokenUsingDeviceCode = useCallback(async () => {
    if (deviceCode) {
      setStatus(DeviceCodeProgress.TOKEN_REQUESTED);
      try {
        // Create AbortController for this specific request
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const result = await authService.exchangeDeviceCode(
          deviceCode,
          controller.signal,
        );

        // Handle error responses
        if (isDeviceCodeError(result)) {
          if (result.error === Auth0DeviceCodeError.AUTHORIZATION_PENDING) {
            // Continue polling - this is expected during the flow
            return;
          } else if (result.error === Auth0DeviceCodeError.SLOW_DOWN) {
            // Slowdown should increase the polling interval.
            // Back off per Auth0 guidance
            // Increase polling interval by a defined backoff step
            const newInterval = Math.min(
              tokenPollInterval + POLLING_BACKOFF_STEP_MS,
              MAX_POLL_INTERVAL_MS,
            );

            setTokenPollInterval(newInterval);
            restartPolling(newInterval);
            return;
          } else if (result.error === Auth0DeviceCodeError.EXPIRED_TOKEN) {
            cancelPairing();
            setError('Device code expired. Please try again.');
            return;
          } else if (result.error === Auth0DeviceCodeError.ACCESS_DENIED) {
            cancelPairing();
            setError('Authorization was denied. Please try again.');
            return;
          }
          // For other errors, continue polling
          console.error('Unexpected error during token exchange:', result);
          return;
        }

        // Handle successful token response
        if (!controller.signal.aborted && result && isAuthTokens(result)) {
          // Authentication successful - stop polling immediately
          stopPolling();
          await authService.createSession(result, controller.signal);
          setStatus(DeviceCodeProgress.TOKEN_RECEIVED);
          setError(null);
        }
      } catch (error) {
        // Don't update state if request was aborted
        if (
          abortControllerRef.current &&
          !abortControllerRef.current.signal.aborted
        ) {
          setError(
            error instanceof Error ? error.message : 'Authentication failed',
          );
        }
      }
    }

    // stopPolling will be available from usePolling hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceCode, tokenPollInterval]);

  const tokenPollingProps: UsePollingProps = {
    callback: getTokenUsingDeviceCode,
  };
  const { startPolling, stopPolling, restartPolling } =
    usePolling(tokenPollingProps);

  const getDeviceCode = useCallback(async () => {
    try {
      // Cancel any existing request
      abortCurrentRequest();

      // Create new AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await authService.initiateDeviceCodeFlow(
        controller.signal,
      );

      // Only update state if request wasn't aborted
      if (!controller.signal.aborted) {
        // Update state with device code info
        setDeviceCode(response.device_code);
        setUserCode(response.user_code);
        setQrCodeUrl(response.verification_uri_complete);
        setTimeRemaining(response.expires_in); // seconds
        setTokenPollInterval(response.interval * 1000); // Auth0 recommendation (ms to seconds)
        setIsPolling(true);
        setError(null);
        setStatus(DeviceCodeProgress.DEVICE_CODE_RECEIVED);

        // Countdown will be handled by useEffect
      }
    } catch (error) {
      // Don't update state if request was aborted
      if (
        abortControllerRef.current &&
        !abortControllerRef.current.signal.aborted
      ) {
        setIsPolling(false);
        return;
      }
      console.error('Error initiating device code flow:', error);
    }
  }, [abortCurrentRequest]); // Cancel and reset everything
  const cancelPairing = useCallback(() => {
    console.info('Canceling device code pairing');

    // Stop all polling activities
    stopPolling();

    // Abort any ongoing network requests
    abortCurrentRequest();

    setIsPolling(false);
    setError(null);
    setTokenPollInterval(0);
    setStatus(DeviceCodeProgress.CANCELLED);

    console.info('Device code pairing canceled and reset');
  }, [abortCurrentRequest, stopPolling]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      console.info('useDeviceCodeAuth: Cleaning up on unmount');
      // Stop all polling activities
      stopPolling();

      setStatus(DeviceCodeProgress.IDLE);
      // Abort any ongoing network requests
      abortCurrentRequest();
      // No need to reset state on unmount, component is going away
    };
  }, [abortCurrentRequest, stopPolling]);

  // Handle device code expiration
  useEffect(() => {
    if (timeRemaining === 0 && deviceCode) {
      // Stop the token polling
      stopPolling();

      setDeviceCode(null);
      setError('Device code expired, request a new one');
      setIsPolling(false);
      setStatus(DeviceCodeProgress.DEVICE_CODE_EXPIRED);
    }
  }, [timeRemaining, deviceCode, stopPolling]);

  // Handle countdown timer
  useEffect(() => {
    if (
      timeRemaining > 0 &&
      deviceCode &&
      status !== DeviceCodeProgress.TOKEN_RECEIVED
    ) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [timeRemaining, deviceCode, status]);

  // Start token polling when device code is available
  useEffect(() => {
    if (
      deviceCode &&
      status === DeviceCodeProgress.DEVICE_CODE_RECEIVED &&
      !error &&
      tokenPollInterval > 0
    ) {
      startPolling(tokenPollInterval);
      setStatus(DeviceCodeProgress.TOKEN_REQUESTED);
    }
  }, [deviceCode, error, startPolling, status, tokenPollInterval]);

  const initiateDeviceCodeFlow = useCallback(
    async (opts?: { retry: boolean }) => {
      if (opts?.retry) {
        console.info('Retrying device code flow');
        // Stop any existing polling
        stopPolling();
        // Abort any pending requests
        abortCurrentRequest();
        setError(null);
        setDeviceCode(null);
      }
      await getDeviceCode();
    },
    [getDeviceCode, stopPolling, abortCurrentRequest],
  );

  // Emit state changes via callback whenever state changes
  useEffect(() => {
    if (onStateChange && status === DeviceCodeProgress.DEVICE_CODE_RECEIVED) {
      const stateUpdate: DeviceCodeStateUpdate = {
        deviceCode,
        userCode,
        qrCodeUrl,
        isPolling,
        timeRemaining,
        error,
      };
      onStateChange(stateUpdate);
    }
  }, [
    deviceCode,
    userCode,
    qrCodeUrl,
    isPolling,
    timeRemaining,
    error,
    onStateChange,
    status,
  ]);

  const value = useMemo(
    () => ({
      deviceCode,
      userCode,
      qrCodeUrl,
      isPolling,
      timeRemaining,
      error,
      status,
      initiateDeviceCodeFlow,
      cancelPairing,
      resetDeviceCode,
    }),
    [
      deviceCode,
      userCode,
      qrCodeUrl,
      isPolling,
      timeRemaining,
      error,
      status,
      initiateDeviceCodeFlow,
      cancelPairing,
      resetDeviceCode,
    ],
  );

  return value;
};
